/**
 * UI Slice - handles UI state like sidebar, theme, language, etc.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type ThemeMode = 'light' | 'dark';
type Language = 'en' | 'ar';

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  themeMode: ThemeMode;
  language: Language;
}

const getInitialTheme = (): ThemeMode => {
  const stored = localStorage.getItem('themeMode');
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return 'light';
};

const getInitialLanguage = (): Language => {
  const stored = localStorage.getItem('i18nextLng') || localStorage.getItem('language');
  if (stored === 'en' || stored === 'ar') {
    return stored;
  }
  return 'en';
};

const initialState: UIState = {
  /** Mobile overlay drawer: closed until user opens via menu */
  sidebarOpen: false,
  sidebarCollapsed: false,
  themeMode: getInitialTheme(),
  language: getInitialLanguage(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
      localStorage.setItem('themeMode', action.payload);
    },
    toggleThemeMode: (state) => {
      state.themeMode = state.themeMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', state.themeMode);
    },
    setLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
      localStorage.setItem('i18nextLng', action.payload);
      localStorage.setItem('language', action.payload);
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setSidebarCollapsed,
  setThemeMode,
  toggleThemeMode,
  setLanguage,
} = uiSlice.actions;

export default uiSlice.reducer;

